import apiClient from "../../apis/tenant-management/tenant-management-client";
import { toast } from "react-toastify";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import { UserEntity } from "../../domain/user/user-entity";
import { rebuildQuery } from "../../../utils/url-utils";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../domain/pagination/paging";
import { CloudGovApiError } from "../../domain/api-error";
import FileSaver from "file-saver";
import {
  nameCsvFile,
  nameCsvFileWithResourceId,
} from "../../../utils/save-utils";
import { reportUnexpectedError } from "../../../error-reporter";
import { convert } from "encoding-japanese";
import {
  UserBatchImportResult,
  UserBatchUpdateResult,
} from "../../domain/user/user-batch-import-result";

export type OrganizationUserServiceProps = {
  isLoading: boolean;
  isBatchImporting: boolean;
  paging: {
    current: number;
    total: number;
  };
  query: string;
  organizationUsers: Array<UserEntity> | undefined;
  fetchUsersByOrganizationId: typeof OrganizationUserService.prototype.fetchUsersByOrganizationId;
  searchUsers: typeof OrganizationUserService.prototype.searchUsers;
  enableOrganizationUser: typeof OrganizationUserService.prototype.enableOrganizationUser;
  disableOrganizationUser: typeof OrganizationUserService.prototype.disableOrganizationUser;
  setPage: typeof OrganizationUserService.prototype.setPage;
  movePage: typeof OrganizationUserService.prototype.movePage;
  setQuery: typeof OrganizationUserService.prototype.setQuery;
  importByCsv: typeof OrganizationUserService.prototype.importByCsv;
  updateByCsv: typeof OrganizationUserService.prototype.updateByCsv;
  onDownloadCsv: typeof OrganizationUserService.prototype.onDownloadCsv;
  downloadTemplateCsv: typeof OrganizationUserService.prototype.downloadTemplateCsv;
};

export class OrganizationUserService extends BaseApplicationService<OrganizationUserServiceProps> {
  state = {
    isLoading: false,
    isBatchImporting: false,
    paging: {
      current: 1,
      total: 0,
    },
    query: "",
    organizationUsers: [],
    groups: [],
    fetchUsersByOrganizationId: this.fetchUsersByOrganizationId.bind(this),
    searchUsers: this.searchUsers.bind(this),
    enableOrganizationUser: this.enableOrganizationUser.bind(this),
    disableOrganizationUser: this.disableOrganizationUser.bind(this),
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    setQuery: this.setQuery.bind(this),
    importByCsv: this.importByCsv.bind(this),
    updateByCsv: this.updateByCsv.bind(this),
    onDownloadCsv: this.onDownloadCsv.bind(this),
    downloadTemplateCsv: this.downloadTemplateCsv.bind(this),
  };

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  setQuery(nameQuery: string) {
    this.setState({
      query: nameQuery,
    });
  }

  async fetchUsersByOrganizationId(organizationId: string) {
    this.setIsLoading(true);
    try {
      const { data: organizationUsers, headers } =
        await apiClient.fetchUsersByOrganizationId(
          organizationId,
          this.state.paging.current,
          DefaultNumItemsPerPages,
          {
            q: this.state.query ? this.state.query : undefined,
          },
        );
      this.setState({
        organizationUsers: organizationUsers || [],
        paging: {
          ...this.state.paging,
          total: toTotalPage(Number(headers["pagination-count"])),
        },
      });
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("ユーザーの取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  searchUsers() {
    this.history.push(
      rebuildQuery({
        page: "1",
        q: this.state.query,
      }),
    );
  }

  async disableOrganizationUser(organizationUserId: string) {
    this.setIsLoading(true);
    try {
      await apiClient.disableUser(organizationUserId);
      toast.success("テナントユーザーの権限を無効にしました");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("権限の変更に失敗しました");
    } finally {
      this.setIsLoading(false);
    }
  }

  async enableOrganizationUser(organizationUserId: string) {
    this.setIsLoading(true);
    try {
      await apiClient.enableUser(organizationUserId);
      toast.success("テナントユーザーの権限を有効にしました");
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("権限の変更に失敗しました");
    } finally {
      this.setIsLoading(false);
    }
  }

  async importByCsv(
    organizationId: string,
    csv: string,
    validateOnly: boolean,
    doSendInvitation: boolean,
  ): Promise<UserBatchImportResult | undefined> {
    this.setState({
      isBatchImporting: true,
    });
    try {
      const { data } = await apiClient.importUsersByCsv(
        organizationId,
        csv,
        validateOnly,
        doSendInvitation,
      );
      if (!validateOnly) await this.fetchUsersByOrganizationId(organizationId);
      return data;
    } catch (e) {
      await this.handleBatchImportError(e);
      return undefined;
    } finally {
      this.setState({
        isBatchImporting: false,
      });
    }
  }

  private async handleBatchImportError(e: any) {
    const apiError = new CloudGovApiError(e);
    if (CloudGovApiError.is403(apiError)) {
      toast.error("ユーザー一括取込を実行する権限がありません。");
      return;
    }
    if (apiError.errorCode() === "bad_request.invalid_column_length") {
      toast.error("CSVの列数が一致しているか確認してください。");
      return;
    }
    if (apiError.errorCode() === "bad_request.invalid_column_header_label") {
      toast.error("正しいフォーマットのCSVを選択してください。");
      return;
    }
    if (apiError.errorCode() === "bad_request.too_large_rows_count") {
      toast.error(
        "一括操作の対象件数が上限を超えています。1000件以内で実行してください。",
      );
      return;
    }
    reportUnexpectedError(e);
    toast.error("ユーザー一括取込でエラーが発生しました。");
  }

  async updateByCsv(
    organizationId: string,
    csv: string,
    validateOnly: boolean,
  ): Promise<UserBatchUpdateResult | undefined> {
    this.setState({
      isBatchImporting: true,
    });
    try {
      const { data } = await apiClient.updateUsersByCsv(
        organizationId,
        csv,
        validateOnly,
      );
      if (!validateOnly) await this.fetchUsersByOrganizationId(organizationId);
      return data;
    } catch (e) {
      await this.handleBatchImportError(e);
      return undefined;
    } finally {
      this.setState({
        isBatchImporting: false,
      });
    }
  }

  private setIsLoading(isLoading: boolean) {
    this.setState({
      isLoading: isLoading,
    });
  }

  async onDownloadCsv(orgId: string, isOutputUserId: boolean) {
    try {
      const { data } = await apiClient.downloadUsersCsv(orgId, isOutputUserId);
      FileSaver.saveAs(data, nameCsvFileWithResourceId("users", orgId));
    } catch (e) {
      await this.handleCsvDownloadError(e);
    }
  }

  private async handleCsvDownloadError(e: any) {
    const apiError = new CloudGovApiError(e);
    if (CloudGovApiError.is403(apiError)) {
      toast.error("ユーザーをCSV出力する権限がありません。");
      return;
    }

    await apiError.resolveErrorResponse();
    switch (apiError.errorCode()) {
      case "bad_request.too_large_record_count":
        toast.error("ユーザーの件数が出力上限を超えました。");
        return;
    }
    reportUnexpectedError(e);
    toast.error("CSV出力でエラーが発生しました");
  }

  downloadTemplateCsv() {
    const template =
      '"アカウント名","氏名","メールアドレス","ロール","グループID","グループロール","パスワード"\n';
    const unicodeArr: number[] = [];
    for (let i = 0; i < template.length; i++) {
      unicodeArr.push(template.charCodeAt(i));
    }
    const sjisTemplate = new Uint8Array(convert(unicodeArr, "SJIS", "UNICODE"));
    const blob = new Blob([sjisTemplate], { type: "text/csv" });
    FileSaver.saveAs(blob, nameCsvFile("users-template"));
  }
}
